import React, { useState } from 'react';
import { ArrowRight, Check, Loader2, AlertCircle } from 'lucide-react';
import { Section } from './ui/Section';
import { supabase } from '../lib/supabase';

export const Hero: React.FC = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setStatus('loading');
    setErrorMessage('');
    
    try {
      // Attempt to insert email into 'leads' table
      const { error } = await supabase
        .from('leads')
        .insert([{ email }]);

      if (error) {
        // Error 23505 is a unique constraint violation (duplicate email)
        // We treat it as success from a UX perspective to avoid leaking data
        if (error.code !== '23505') {
          throw error;
        }
      }

      setStatus('success');
      setEmail('');

      if (typeof window !== 'undefined' && 'gtag' in window) {
      // @ts-ignore
        window.gtag('event', 'email_signup', {
        event_category: 'engagement',
        event_label: 'early_access'
       });
      }

    } catch (err) {
      console.error('Submission error:', err);
      setStatus('idle');
      setErrorMessage('Unable to join the list at this moment. Please try again.');
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col justify-center border-b border-gray-200 bg-[#F9F8F6] overflow-hidden">
      {/* Animated Architectural Grid Background */}
      <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none animate-slow-pan"
           style={{
             backgroundImage: `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
             backgroundSize: '80px 80px'
           }}
      />
      
      {/* Gradient Overlay to fade grid at edges for elegance */}
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#F9F8F6] via-transparent to-[#F9F8F6] pointer-events-none" />

      <Section className="relative z-10 flex flex-col items-center text-center pt-20 pb-12 md:pt-48 md:pb-32">
        {/* Pill Label */}
        <div className="inline-block px-4 py-1.5 mb-6 md:mb-10 border border-gray-300 rounded-full bg-white/40 backdrop-blur-sm animate-fade-in">
          <span className="text-[10px] md:text-xs font-sans font-medium tracking-[0.2em] text-gray-600 uppercase">
            INDEPENDENT STANDARDS INITIATIVE
          </span>
        </div>

        {/* H1 Headline */}
        <h1 className="flex flex-col gap-2 md:gap-4 mb-6 md:mb-10 animate-fade-in" style={{ animationDelay: '0.1s' }}>
          <span className="text-5xl md:text-7xl lg:text-8xl text-primary leading-none tracking-tight">
            The 'leads' that were never real
          </span>
        </h1>

        {/* Subheadline */}
        <p className="max-w-2xl text-lg md:text-xl text-secondary font-sans font-light leading-relaxed mb-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          An independent effort to bring transparency, clarity, and shared definitions to B2B lead generation - starting with content syndication.
        </p>

        {/* Supporting Text */}
        <p className="max-w-lg text-sm md:text-base text-gray-500 font-sans font-light mb-14 animate-fade-in" style={{ animationDelay: '0.3s' }}>
          Not a product. Not a sales funnel. A standards project for buyers, vendors, and operators who care about the truth behind their data.
        </p>

        {/* Email Form */}
        <div className="w-full max-w-md animate-fade-in" style={{ animationDelay: '0.4s' }}>
          {status === 'success' ? (
            <div className="flex flex-col items-center justify-center p-8 border border-gray-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.02)] rounded-sm">
              <div className="w-10 h-10 flex items-center justify-center bg-primary rounded-full mb-4">
                <Check className="w-5 h-5 text-white" />
              </div>
              <p className="text-primary font-serif text-lg">You are on the list.</p>
              <p className="text-sm text-gray-500 mt-2 font-light">We’ll be in touch when our first report is released.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="relative group z-0">
                <input
                  type="email"
                  placeholder="Enter your email for early access"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 md:px-5 py-3 md:py-5 bg-white border border-gray-300 text-primary placeholder:text-sm md:placeholder:text-base placeholder:text-gray-400 focus:outline-none focus:border-primary focus:ring-0 transition-colors rounded-sm shadow-sm text-base font-light disabled:opacity-50"
                  required
                  disabled={status === 'loading'}
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="absolute right-2 top-2 bottom-2 z-10 bg-primary hover:bg-black text-white px-6 flex items-center justify-center transition-all disabled:opacity-70 rounded-sm"
                  style={{ backgroundColor: '#111111', color: '#FFFFFF' }}
                >
                  {status === 'loading' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <span className="flex items-center gap-2 text-sm font-medium tracking-wide">
                      Join <ArrowRight className="w-3 h-3" />
                    </span>
                  )}
                </button>
              </div>
              
              {errorMessage && (
                <div className="flex items-center gap-2 text-red-600 text-sm mt-2">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errorMessage}</span>
                </div>
              )}

              <p className="text-[11px] text-gray-400 font-sans tracking-wide uppercase opacity-80">
                No spam. No pitches. Research & standards only.
              </p>
            </form>
          )}
        </div>
      </Section>
    </div>
  );
};